import { ACCOUNT_ACTIONS } from '../actions/account'

const initialState = {
  // Legacy structure for backward compatibility
  accountInfo: {
    name: '',
    email: '',
    subdomain: '',
    planName: '',
    accountType: 'Business',
    status: '',
    createdDate: '',
    company: '',
    timezone: '',
    displayConsumerDesignation: false
  },
  usageStats: {
    entries: 0,
    entriesLimit: 0,
    entriesPercent: 0,
    entriesStatus: 'green',
    forms: 0,
    formsLimit: 0,
    reports: 0,
    reportsLimit: 0,
    storage: 0,
    storageLimit: 0,
    resetDate: null
  },
  planInfo: {
    planId: null,
    name: '',
    price: 0,
    currencySymbol: '$',
    currencyCode: 'USD',
    interval: 'monthly',
    isFree: true,
    planColor: 'gray',
    discount: null,
    features: {}
  },
  billingInfo: {
    billingEmail: '',
    taxStatus: null,
    billingInfoAvailable: false,
    upcomingInvoice: null
  },
  bills: [],

  // Loading states
  isLoading: false,
  error: null,
  isUpdating: false,
  isVerifyingEmail: false,

  // New API-specific loading states
  profile: {
    loading: false,
    data: null,
    error: null
  },
  usage: {
    loading: false,
    data: null,
    error: null
  },
  plan: {
    loading: false,
    data: null,
    error: null
  },
  billing: {
    loading: false,
    data: null,
    error: null
  },
  invoices: {
    loading: false,
    data: null,  // Will contain { invoices: [...], currencies: {...} }
    error: null
  },

  // Subdomain operations
  subdomain: {
    checking: false,
    changing: false,
    checkResult: null,
    changeResult: null,
    error: null
  },

  // Account type operations
  accountType: {
    changing: false,
    result: null,
    error: null
  }
}

export default function accountReducer(state = initialState, action) {
  switch (action.type) {
    // Get account data
    case ACCOUNT_ACTIONS.GET_ACCOUNT_DATA_REQUEST:
      return {
        ...state,
        isLoading: true,
        error: null
      }

    case ACCOUNT_ACTIONS.GET_ACCOUNT_DATA_SUCCESS:
      return {
        ...state,
        isLoading: false,
        accountInfo: action.payload.accountInfo,
        usageStats: action.payload.usageStats,
        planInfo: action.payload.planInfo,
        billingInfo: action.payload.billingInfo,
        bills: action.payload.bills,
        error: null
      }

    case ACCOUNT_ACTIONS.GET_ACCOUNT_DATA_FAILURE:
      return {
        ...state,
        isLoading: false,
        error: action.payload
      }

    // Update account info
    case ACCOUNT_ACTIONS.UPDATE_ACCOUNT_INFO_REQUEST:
      return {
        ...state,
        isUpdating: true,
        error: null
      }

    case ACCOUNT_ACTIONS.UPDATE_ACCOUNT_INFO_SUCCESS:
      return {
        ...state,
        isUpdating: false,
        accountInfo: {
          ...state.accountInfo,
          ...action.payload
        },
        error: null
      }

    case ACCOUNT_ACTIONS.UPDATE_ACCOUNT_INFO_FAILURE:
      return {
        ...state,
        isUpdating: false,
        error: action.payload
      }

    // Verify email
    case ACCOUNT_ACTIONS.VERIFY_EMAIL_REQUEST:
      return {
        ...state,
        isVerifyingEmail: true,
        error: null
      }

    case ACCOUNT_ACTIONS.VERIFY_EMAIL_SUCCESS:
      return {
        ...state,
        isVerifyingEmail: false,
        error: null
      }

    case ACCOUNT_ACTIONS.VERIFY_EMAIL_FAILURE:
      return {
        ...state,
        isVerifyingEmail: false,
        error: action.payload
      }

    // Profile API actions
    case ACCOUNT_ACTIONS.GET_PROFILE_REQUEST:
      return {
        ...state,
        profile: { ...state.profile, loading: true, error: null }
      }
    case ACCOUNT_ACTIONS.GET_PROFILE_SUCCESS:
      return {
        ...state,
        profile: { loading: false, data: action.payload, error: null }
      }
    case ACCOUNT_ACTIONS.GET_PROFILE_FAILURE:
      return {
        ...state,
        profile: { loading: false, data: null, error: action.payload }
      }

    // Usage API actions
    case ACCOUNT_ACTIONS.GET_USAGE_REQUEST:
      return {
        ...state,
        usage: { ...state.usage, loading: true, error: null }
      }
    case ACCOUNT_ACTIONS.GET_USAGE_SUCCESS:
      return {
        ...state,
        usage: { loading: false, data: action.payload, error: null }
      }
    case ACCOUNT_ACTIONS.GET_USAGE_FAILURE:
      return {
        ...state,
        usage: { loading: false, data: null, error: action.payload }
      }

    // Plan API actions
    case ACCOUNT_ACTIONS.GET_PLAN_REQUEST:
      return {
        ...state,
        plan: { ...state.plan, loading: true, error: null }
      }
    case ACCOUNT_ACTIONS.GET_PLAN_SUCCESS:
      return {
        ...state,
        plan: { loading: false, data: action.payload, error: null }
      }
    case ACCOUNT_ACTIONS.GET_PLAN_FAILURE:
      return {
        ...state,
        plan: { loading: false, data: null, error: action.payload }
      }

    // Billing API actions
    case ACCOUNT_ACTIONS.GET_BILLING_REQUEST:
      return {
        ...state,
        billing: { ...state.billing, loading: true, error: null }
      }
    case ACCOUNT_ACTIONS.GET_BILLING_SUCCESS:
      return {
        ...state,
        billing: { loading: false, data: action.payload, error: null }
      }
    case ACCOUNT_ACTIONS.GET_BILLING_FAILURE:
      return {
        ...state,
        billing: { loading: false, data: null, error: action.payload }
      }

    // Invoices API actions
    case ACCOUNT_ACTIONS.GET_INVOICES_REQUEST:
      return {
        ...state,
        invoices: { ...state.invoices, loading: true, error: null }
      }
    case ACCOUNT_ACTIONS.GET_INVOICES_SUCCESS:
      return {
        ...state,
        invoices: { loading: false, data: action.payload, error: null }
      }
    case ACCOUNT_ACTIONS.GET_INVOICES_FAILURE:
      return {
        ...state,
        invoices: { loading: false, data: null, error: action.payload }
      }

    // Subdomain check actions
    case ACCOUNT_ACTIONS.CHECK_SUBDOMAIN_REQUEST:
      return {
        ...state,
        subdomain: { ...state.subdomain, checking: true, checkResult: null, error: null }
      }
    case ACCOUNT_ACTIONS.CHECK_SUBDOMAIN_SUCCESS:
      return {
        ...state,
        subdomain: { ...state.subdomain, checking: false, checkResult: action.payload, error: null }
      }
    case ACCOUNT_ACTIONS.CHECK_SUBDOMAIN_FAILURE:
      return {
        ...state,
        subdomain: { ...state.subdomain, checking: false, checkResult: null, error: action.payload }
      }

    // Subdomain change actions
    case ACCOUNT_ACTIONS.CHANGE_SUBDOMAIN_REQUEST:
      return {
        ...state,
        subdomain: { ...state.subdomain, changing: true, changeResult: null, error: null }
      }
    case ACCOUNT_ACTIONS.CHANGE_SUBDOMAIN_SUCCESS:
      return {
        ...state,
        subdomain: { ...state.subdomain, changing: false, changeResult: action.payload, error: null }
      }
    case ACCOUNT_ACTIONS.CHANGE_SUBDOMAIN_FAILURE:
      return {
        ...state,
        subdomain: { ...state.subdomain, changing: false, changeResult: null, error: action.payload }
      }

    // Account type change actions
    case ACCOUNT_ACTIONS.CHANGE_ACCOUNT_TYPE_REQUEST:
      return {
        ...state,
        accountType: { ...state.accountType, changing: true, result: null, error: null }
      }
    case ACCOUNT_ACTIONS.CHANGE_ACCOUNT_TYPE_SUCCESS:
      return {
        ...state,
        accountType: { changing: false, result: action.payload, error: null },
        // Update legacy accountInfo for backward compatibility
        accountInfo: {
          ...state.accountInfo,
          accountType: action.payload.accountType
        }
      }
    case ACCOUNT_ACTIONS.CHANGE_ACCOUNT_TYPE_FAILURE:
      return {
        ...state,
        accountType: { changing: false, result: null, error: action.payload }
      }

    default:
      return state
  }
}
